import React, { useEffect, useState, useCallback } from 'react';
import { Form, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'store/store';
import { alternateLogin, clearAuthState, login } from 'store/slices/authSlice';
import { setGlobalOrgId } from 'store/slices/globalOrgSlice';
import { describeLocation, describeLocationWorkflow, fetchLocations } from 'store/slices/locationsSlice';
import { describeUser, fetchSelectedUserSites } from 'store/slices/usersSlice';
import { fetchSubscriptions } from 'store/slices/subscriptionsSlice';
import { LoginPayload } from 'types/authTypes';
import LoginForm from './LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import AvigilonAltaLogin from './AvigilonAltaLogin';
import AlternateLogin from './AlternateLogin';
import { useTranslation } from 'react-i18next';
import './login.scss';
import { getFirstAvailableRoute } from 'utils/routing';
import Loader from 'components/customAntd/Loader';

const appVersion = process.env.REACT_APP_VERSION;

const LogIn: React.FC = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const { auth, error, loginLoading } = useSelector((state: RootState) => state.auth);

    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [isAlternateLogin, setIsAlternateLogin] = useState(false);
    const [isAvigilonLogin, setIsAvigilonLogin] = useState(false);
    const { isGuestPassIntegrated, isTokenValid } = useSelector((state: RootState) => state.integration);
    const [isLoadingOrgData, setIsLoadingOrgData] = useState(false);

    const { t } = useTranslation();

    const handleBackToLogin = () => {
        setIsAlternateLogin(false);
        setIsAvigilonLogin(false);
    };

    const handleLearnMoreClick = () => {
        window.open("https://invisit.us", "_blank");
    };

    // Inside the LogIn component, add these selectors
    const globalUser = useSelector((state: RootState) => state.users.globalUser);
    const globalLocation = useSelector((state: RootState) => state.locations.globalLocation);
    const globalOrgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);

    useEffect(() => {
        if (auth.data.length > 0 && globalUser && globalLocation && globalOrgId) {
            const tokenScopeList = auth.data[0].tokenScopeList || [];
            const route = getFirstAvailableRoute(tokenScopeList, globalOrgId, isGuestPassIntegrated, isTokenValid);
            navigate(route, { replace: true });
        }
    }, [auth.data, globalUser, globalLocation, globalOrgId, isGuestPassIntegrated, isTokenValid, navigate]);

    const handleOrganizationSelection = useCallback(async (orgId: number, userId: number) => {
        setIsLoadingOrgData(true);
        try {
            await dispatch(setGlobalOrgId(orgId));
            await dispatch(describeUser({ orgId, userId, global: true }));
            await dispatch(fetchSubscriptions({ orgId }));

            const selectedUserSites = await dispatch(fetchSelectedUserSites({ orgId, userId }));
            const locationsResult = await dispatch(fetchLocations({ orgId, status: 1 }));

            if (fetchSelectedUserSites.fulfilled.match(selectedUserSites) && selectedUserSites.payload.length > 0) {
                const defaultLocationId = selectedUserSites.payload?.[0].id;
                if (defaultLocationId) {
                    await dispatch(describeLocation({ orgId, locationId: defaultLocationId, global: true }));
                    await dispatch(describeLocationWorkflow({ orgId, locationId: defaultLocationId, global: true }));
                }
            } else if (fetchLocations.fulfilled.match(locationsResult) && locationsResult.payload.data.length !== 0) {
                const firstLocation = locationsResult.payload.data[0];
                if (firstLocation.id) {
                    await dispatch(describeLocation({ orgId, locationId: firstLocation.id, global: true }));
                    await dispatch(describeLocationWorkflow({ orgId, locationId: firstLocation.id, global: true }));
                }
            }

            const tokenScopeList = auth.data[0].tokenScopeList || [];
            const route = getFirstAvailableRoute(tokenScopeList, orgId, isGuestPassIntegrated, isTokenValid);
            navigate(route, { replace: true });
        } catch (error) {
            console.error('Error during organization selection:', error);
        } finally {
            setIsLoadingOrgData(false);
        }
    }, [auth, isGuestPassIntegrated, isTokenValid, dispatch, navigate]);

    useEffect(() => {
        if (auth.data.length === 1) {
            const tokenScopeList = auth.data[0].tokenScopeList;
            if (tokenScopeList.length === 1) {
                const singleOrg = tokenScopeList[0].org;
                const singleUser = tokenScopeList[0].user;
                if (singleOrg.id != null && singleUser.id != null) {
                    handleOrganizationSelection(singleOrg.id, singleUser.id);
                }
            }
        }
    }, [auth.data, handleOrganizationSelection]);


    const handleLogin = async (values: LoginPayload) => {
        await dispatch(login(values))
    };

    const handleAlternateLogin = async (values: { email: string; password: string; msiAltaApiHost: string; code?: string }) => {
        await dispatch(alternateLogin(values));
    };

    const handleBack = () => {
        dispatch(clearAuthState());
    };

    const isAutoSelecting = auth.data.length === 1 && auth.data[0].tokenScopeList?.length === 1;

    return (
        <div className="auth-wrapper">
            <div className="auth-wrapper__left">
                <div className="auth-wrapper__left-content">
                    {/* Marketing text or other content */}
                    <div className="auth-wrapper__left-content__text">
                        <div className="auth-wrapper__left-content__headline">
                            {t('auth.headline')}
                        </div>
                        <div className="auth-wrapper__left-content__body">
                            {t('auth.body')}
                        </div>
                        <Button
                            onClick={handleLearnMoreClick}
                            type="primary"
                            className="button"
                            style={{ width: "auto" }}
                        >
                            {t('auth.learnMore')}
                        </Button>
                    </div>
                </div>
            </div>
            <div className="auth-wrapper__right">
                <div className="auth-wrapper__right-content">
                    <div className="auth-wrapper__right-logo">
                        <img src="/images/alta_visitor_vertical.png" alt="Logo" style={{ width: '120px', height: 'auto' }} />
                    </div>
                    <div className="login">
                        {loginLoading || isLoadingOrgData || isAutoSelecting ? (
                            <Loader />
                        ) : (
                            <>
                                {isForgotPassword ? (
                                    <ForgotPasswordForm setIsForgotPassword={setIsForgotPassword} />
                                ) : isAlternateLogin && auth.data.length === 0 ? (
                                    isAvigilonLogin ? (
                                        <AvigilonAltaLogin onFinish={handleAlternateLogin} onBack={handleBackToLogin} error={error} />
                                    ) : (
                                        <AlternateLogin onBack={handleBackToLogin} onAvigilonClick={() => setIsAvigilonLogin(true)} />
                                    )
                                ) : auth.data.length === 0 ? (
                                    <LoginForm
                                        onFinish={handleLogin}
                                        error={error}
                                        setIsForgotPassword={setIsForgotPassword}
                                        setIsAlternateLogin={setIsAlternateLogin}
                                    />
                                ) : (
                                    <>
                                        {auth.data[0].tokenScopeList.map((item: any) => (
                                            <Form.Item key={item.org.id}>
                                                <Button
                                                    type="primary"
                                                    className="button"
                                                    onClick={() => handleOrganizationSelection(item.org.id, item.user.id)}
                                                >
                                                    {item.org.name}
                                                </Button>
                                            </Form.Item>
                                        ))}
                                        <Button onClick={handleBack} className="button">{t('Back')}</Button>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    {appVersion && (
                        <div className="version-number" style={{ textAlign: 'center', marginTop: '80px', fontSize: '12px', color: '#888' }}>
                            Build {appVersion}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LogIn;
